import { FileText, MessageSquare, CreditCard, Shield, AlertTriangle, Mail } from "lucide-react";

const LAST_UPDATED = "September 29, 2026";
const CONTACT_EMAIL = "legal@appointmentflo.com";
const SUPPORT_EMAIL = "support@appointmentflo.com";
const COMPANY_NAME = "Digital Empire Marketing LLC";
const PLATFORM_NAME = "Appointment Flo";
const PLATFORM_URL = "https://appointmentflo.com";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#273240] text-white py-6 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <FileText size={28} className="text-blue-400" />
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-gray-300 text-sm">
            {PLATFORM_NAME} · Last Updated: {LAST_UPDATED}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-10">

          {/* Intro */}
          <section>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service ("Terms") constitute a legally binding agreement between you and{" "}
              <strong>{COMPANY_NAME}</strong> ("we," "us," "our"), operating as <strong>{PLATFORM_NAME}</strong>,
              governing your access to and use of the Platform at <a href={PLATFORM_URL} className="text-blue-600 underline">{PLATFORM_URL}</a> and
              its associated subdomains (collectively, the "Platform").
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Please read these Terms carefully before using the Platform. By creating an account, accessing the Platform,
              or using any of our services — including our SMS messaging program — you agree to be bound by these Terms and
              our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>. If you do not agree, do not use
              the Platform.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              These Terms apply to all users, including: (a) businesses and their staff who use the Platform to manage
              appointments ("Tenants"); and (b) end-users who book appointments through the Platform ("Clients").
            </p>
          </section>

          <Divider />

          {/* 1 - Description of Service */}
          <section>
            <SectionHeader icon={<FileText size={20} />} number="1" title="Description of Service" />
            <p className="text-gray-700 mb-4">
              {PLATFORM_NAME} is a multi-tenant Software-as-a-Service (SaaS) appointment scheduling and management platform
              designed for aesthetic clinics and medical spas. The Platform provides:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Online appointment booking wizard (embeddable on tenant websites)</li>
              <li>Staff dashboard with calendar, client management (mini CRM), and analytics</li>
              <li>AI-powered chatbot for natural-language appointment scheduling</li>
              <li>Intake and consent form creation, distribution, and e-signature collection</li>
              <li>Automated SMS and email notifications (booking confirmations, reminders, cancellations)</li>
              <li>Payment processing for deposits and no-show fees via Stripe Connect</li>
              <li>Post-visit satisfaction rating and review solicitation tools</li>
              <li>Provider scheduling and multi-location management</li>
              <li>Business intelligence and revenue analytics</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We reserve the right to modify, suspend, or discontinue any feature or the Platform itself at any time with
              reasonable notice to active Tenants.
            </p>
          </section>

          <Divider />

          {/* 2 - Account Registration */}
          <section>
            <SectionHeader icon={<Shield size={20} />} number="2" title="Account Registration & Access" />

            <Subsection title="2.1 Eligibility">
              <p className="text-gray-700">
                You must be at least 18 years old and capable of forming a binding contract to create a Tenant account.
                By registering, you represent that you have the authority to bind your business to these Terms.
              </p>
            </Subsection>

            <Subsection title="2.2 Account Security">
              <p className="text-gray-700">
                You are responsible for maintaining the confidentiality of your login credentials. You agree to notify us
                immediately at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">{SUPPORT_EMAIL}</a> if
                you suspect unauthorized access to your account. We are not liable for any loss resulting from unauthorized
                use of your account where you failed to maintain credential security.
              </p>
            </Subsection>

            <Subsection title="2.3 Accurate Information">
              <p className="text-gray-700">
                You agree to provide accurate, current, and complete information when creating your account and to update it
                promptly if it changes. Accounts created with false information may be suspended or terminated.
              </p>
            </Subsection>

            <Subsection title="2.4 Team Members">
              <p className="text-gray-700">
                Tenant owners may invite staff members to access the Platform under their account. The Tenant owner is
                responsible for all activity conducted by invited team members and for ensuring team members comply with
                these Terms.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 3 - Acceptable Use */}
          <section>
            <SectionHeader icon={<AlertTriangle size={20} />} number="3" title="Acceptable Use Policy" />
            <p className="text-gray-700 mb-4">You agree NOT to use the Platform to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Violate any applicable federal, state, or local law or regulation</li>
              <li>Send unsolicited commercial messages (spam) or messages to individuals who have not opted in</li>
              <li>Collect or store personal data beyond what is necessary to operate your appointment booking business</li>
              <li>Transmit any content that is unlawful, harassing, abusive, threatening, defamatory, obscene, or otherwise objectionable</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
              <li>Attempt to gain unauthorized access to any part of the Platform or any other user's account</li>
              <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Platform</li>
              <li>Use the Platform to schedule or promote services that are illegal, fraudulent, or harmful</li>
              <li>Overload, disrupt, or impair the Platform's infrastructure or servers</li>
              <li>Use automated bots or scrapers to access or collect data from the Platform without our written consent</li>
              <li>Resell, sublicense, or white-label the Platform without a separate written agreement with us</li>
              <li>Use the AI chatbot to provide medical advice, diagnoses, or health recommendations</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We reserve the right to suspend or terminate any account that violates this Acceptable Use Policy, with or
              without prior notice.
            </p>
          </section>

          <Divider />

          {/* 4 - SMS Program Terms */}
          <section>
            <SectionHeader icon={<MessageSquare size={20} />} number="4" title="SMS Messaging Program Terms" />

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-5">
              <p className="text-blue-900 font-medium text-sm">
                This section governs the SMS notification program operated by {PLATFORM_NAME}.
                It applies to both Tenants who use our SMS features and Clients who receive messages through the Platform.
              </p>
            </div>

            <Subsection title="4.1 Program Description">
              <p className="text-gray-700">
                {PLATFORM_NAME} operates an SMS notification program that sends appointment-related text messages to clients
                who have provided explicit opt-in consent. Messages are sent from telephone numbers registered to
                {PLATFORM_NAME} via Telnyx, Inc.
              </p>
            </Subsection>

            <Subsection title="4.2 Consent Requirement (For Tenants)">
              <p className="text-gray-700">
                Tenants who enable SMS notifications agree to the following:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mt-2">
                <li>SMS messages will only be sent to clients who have explicitly opted in via the booking wizard's SMS consent checkbox.</li>
                <li>Tenants will not use the SMS feature to send marketing, promotional, or non-appointment-related messages.</li>
                <li>Tenants are responsible for ensuring their use of the SMS feature complies with all applicable laws including the Telephone Consumer Protection Act (TCPA), CAN-SPAM Act, and applicable state laws.</li>
                <li>Tenants will maintain opt-in records as required by applicable regulations.</li>
              </ul>
            </Subsection>

            <Subsection title="4.3 Opt-In (For Clients)">
              <p className="text-gray-700">
                By checking the SMS consent checkbox during the booking process, you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mt-2">
                <li>Confirm the phone number provided is yours or that you are authorized to receive texts at that number.</li>
                <li>Consent to receive automated appointment reminder text messages from {PLATFORM_NAME}.</li>
                <li>Acknowledge that your consent is not a condition of booking an appointment.</li>
                <li>Understand that standard message and data rates may apply from your mobile carrier.</li>
              </ul>
            </Subsection>

            <Subsection title="4.4 Types of Messages & Frequency">
              <p className="text-gray-700">
                Messages sent are strictly appointment-related: booking confirmations, 24-hour reminders, 1-hour reminders,
                cancellation notices, reschedule confirmations, and post-visit satisfaction requests. Clients receive
                approximately <strong>2–4 messages per appointment</strong>. No marketing or promotional SMS messages are sent.
              </p>
            </Subsection>

            <Subsection title="4.5 Opt-Out">
              <p className="text-gray-700">
                Reply <strong>STOP</strong> to any message to opt out. You will receive a single confirmation and receive
                no further messages. Reply <strong>HELP</strong> for support. You may also email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">{SUPPORT_EMAIL}</a> to opt out.
              </p>
            </Subsection>

            <Subsection title="4.6 Carrier Rates">
              <p className="text-gray-700">
                <strong>Message and data rates may apply.</strong> {PLATFORM_NAME} does not charge for SMS messages, but
                your mobile carrier may charge you for sending and receiving text messages according to your plan.
              </p>
            </Subsection>

            <Subsection title="4.7 Supported Carriers">
              <p className="text-gray-700">
                Our SMS program is compatible with all major U.S. carriers including AT&T, T-Mobile, Verizon, Sprint,
                Boost Mobile, MetroPCS, and other carriers that support standard SMS/MMS.
              </p>
            </Subsection>

            <Subsection title="4.8 No Guarantee of Delivery">
              <p className="text-gray-700">
                We make commercially reasonable efforts to deliver all SMS messages. However, we cannot guarantee delivery
                due to carrier issues, network outages, incorrect phone numbers, or other factors outside our control.
                We are not liable for missed appointment reminders resulting from undelivered SMS messages.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 5 - Payment Terms */}
          <section>
            <SectionHeader icon={<CreditCard size={20} />} number="5" title="Payment Terms" />

            <Subsection title="5.1 Platform Subscription">
              <p className="text-gray-700">
                Tenant access to the Platform may be subject to subscription fees as described in your service agreement
                or pricing plan. Fees are billed in advance on a recurring basis. All fees are non-refundable except as
                required by applicable law or expressly stated in your service agreement.
              </p>
            </Subsection>

            <Subsection title="5.2 Stripe Connect (For Tenants)">
              <p className="text-gray-700">
                To accept client deposits and process payments through the Platform, Tenants must connect a Stripe account
                via Stripe Connect. By doing so, you agree to{" "}
                <a href="https://stripe.com/legal/connect-account" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                  Stripe's Connected Account Agreement
                </a>{" "}
                and{" "}
                <a href="https://stripe.com/legal" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                  Stripe's Terms of Service
                </a>. {PLATFORM_NAME} is not a payment processor and is not responsible for payment failures, chargebacks,
                or Stripe account suspensions.
              </p>
            </Subsection>

            <Subsection title="5.3 Deposits & No-Show Fees (For Clients)">
              <p className="text-gray-700">
                Some businesses using the Platform require deposits at the time of booking. Deposit amounts, cancellation
                policies, and no-show fee amounts are set by each individual business and are clearly displayed before you
                complete a booking. By completing a booking with a deposit requirement, you agree to that business's
                stated payment terms. Refund eligibility is determined by the business's cancellation policy.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 6 - Data & Privacy */}
          <section>
            <SectionHeader icon={<Shield size={20} />} number="6" title="Data Handling & Privacy" />

            <Subsection title="6.1 Tenant Data Responsibilities">
              <p className="text-gray-700">
                Tenants are the data controllers for their clients' personal information. By using the Platform, Tenants
                represent that they have obtained all necessary consents and have authority to collect, store, and process
                client data in accordance with applicable privacy laws, including HIPAA where applicable to their practice.
                {PLATFORM_NAME} acts as a data processor on behalf of Tenants.
              </p>
            </Subsection>

            <Subsection title="6.2 HIPAA Notice">
              <p className="text-gray-700">
                {PLATFORM_NAME} is not a HIPAA-covered entity and this platform does not constitute a HIPAA-compliant
                Electronic Health Record (EHR) system. While we implement strong security practices including per-tenant
                database isolation and encryption, Tenants who are HIPAA-covered entities are responsible for their own
                compliance obligations. We recommend consulting a healthcare compliance attorney regarding your specific
                requirements.
              </p>
            </Subsection>

            <Subsection title="6.3 Privacy Policy">
              <p className="text-gray-700">
                Our collection and use of personal information is governed by our{" "}
                <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>, which is incorporated into
                these Terms by reference.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 7 - AI Chatbot */}
          <section>
            <SectionHeader icon={<MessageSquare size={20} />} number="7" title="AI-Powered Features" />
            <p className="text-gray-700 mb-4">
              The Platform includes an AI chatbot that assists clients with appointment booking. You understand and agree that:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>The AI chatbot is designed for appointment scheduling and general business FAQ purposes only.</li>
              <li>The chatbot will not provide medical advice, diagnoses, treatment recommendations, or any health-related guidance.</li>
              <li>AI responses may occasionally be inaccurate. Clients should verify important details (dates, times, pricing) through official booking confirmations.</li>
              <li>Conversations with the AI chatbot are stored for platform functionality and may be reviewed by us for safety and quality purposes.</li>
              <li>The chatbot is powered by Cloudflare Workers AI (Llama 3.1) and/or Anthropic Claude as a fallback service.</li>
            </ul>
          </section>

          <Divider />

          {/* 8 - Intellectual Property */}
          <section>
            <SectionHeader icon={<FileText size={20} />} number="8" title="Intellectual Property" />

            <Subsection title="8.1 Our IP">
              <p className="text-gray-700">
                The Platform, including its software, design, features, trademarks, and the "AppointmentFlo" name and logo,
                are owned by {COMPANY_NAME} and are protected by copyright, trademark, and other intellectual property laws.
                You may not copy, reproduce, modify, or create derivative works from any part of the Platform without
                our express written consent.
              </p>
            </Subsection>

            <Subsection title="8.2 Tenant Content">
              <p className="text-gray-700">
                Tenants retain ownership of their business data, logos, brand assets, form templates, and client data
                uploaded to the Platform ("Tenant Content"). By uploading Tenant Content, you grant us a limited,
                non-exclusive license to host, store, and process that content solely to provide the Platform services.
              </p>
            </Subsection>

            <Subsection title="8.3 Feedback">
              <p className="text-gray-700">
                If you provide feedback, suggestions, or ideas about the Platform, you grant us an irrevocable,
                royalty-free license to use that feedback for any purpose without compensation to you.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 9 - Disclaimers */}
          <section>
            <SectionHeader icon={<AlertTriangle size={20} />} number="9" title="Disclaimers & Limitation of Liability" />

            <Subsection title="9.1 As-Is Disclaimer">
              <p className="text-gray-700 uppercase text-sm font-medium">
                The platform is provided "as is" and "as available" without warranties of any kind, express or implied,
                including but not limited to warranties of merchantability, fitness for a particular purpose,
                non-infringement, or uninterrupted operation.
              </p>
            </Subsection>

            <Subsection title="9.2 No Medical Advice">
              <p className="text-gray-700">
                {PLATFORM_NAME} is a scheduling and business management tool. Nothing on the Platform constitutes medical
                advice, and we make no representations regarding the qualifications, credentials, or quality of services
                offered by individual tenant businesses.
              </p>
            </Subsection>

            <Subsection title="9.3 Limitation of Liability">
              <p className="text-gray-700 uppercase text-sm font-medium">
                To the maximum extent permitted by applicable law, {COMPANY_NAME} and its officers, directors, employees,
                and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages,
                including but not limited to lost profits, lost data, business interruption, or loss of goodwill, arising
                from your use of or inability to use the platform, even if advised of the possibility of such damages.
              </p>
              <p className="text-gray-700 mt-3">
                Our total aggregate liability to you for any claims arising from these Terms or your use of the Platform
                shall not exceed the greater of (a) the total fees paid by you to us in the twelve (12) months preceding
                the claim, or (b) $100 USD.
              </p>
            </Subsection>

            <Subsection title="9.4 Third-Party Services">
              <p className="text-gray-700">
                The Platform integrates with third-party services including Stripe, Telnyx, Resend, Cloudflare, and
                Anthropic. We are not responsible for any failure, outage, or error caused by these third-party providers.
                Your use of these services is also governed by their respective terms of service.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 10 - Indemnification */}
          <section>
            <SectionHeader icon={<Shield size={20} />} number="10" title="Indemnification" />
            <p className="text-gray-700">
              You agree to defend, indemnify, and hold harmless {COMPANY_NAME} and its officers, directors, employees,
              and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable
              attorney fees) arising from or related to: (a) your use of the Platform; (b) your violation of these Terms;
              (c) your violation of any applicable law or regulation; (d) any client data you collect, store, or process
              through the Platform; or (e) any claim by a third party related to your business's services or operations.
            </p>
          </section>

          <Divider />

          {/* 11 - Termination */}
          <section>
            <SectionHeader icon={<AlertTriangle size={20} />} number="11" title="Termination" />

            <Subsection title="11.1 By You">
              <p className="text-gray-700">
                You may terminate your Tenant account at any time by contacting us at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">{SUPPORT_EMAIL}</a>.
                Termination does not entitle you to a refund of prepaid subscription fees.
              </p>
            </Subsection>

            <Subsection title="11.2 By Us">
              <p className="text-gray-700">
                We may suspend or terminate your account immediately if: (a) you materially breach these Terms and fail
                to cure the breach within 7 days of notice; (b) you use the Platform for illegal or harmful purposes;
                (c) you fail to pay applicable fees; or (d) we are required to do so by law. Upon termination, your
                right to access the Platform ceases immediately. We will provide 30 days' notice for non-cause terminations.
              </p>
            </Subsection>

            <Subsection title="11.3 Data After Termination">
              <p className="text-gray-700">
                Upon account termination, you may request an export of your data within 30 days. After 30 days, data may
                be deleted in accordance with our retention policy. See our{" "}
                <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a> for details.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 12 - General */}
          <section>
            <SectionHeader icon={<FileText size={20} />} number="12" title="General Provisions" />

            <Subsection title="12.1 Governing Law">
              <p className="text-gray-700">
                These Terms are governed by and construed in accordance with the laws of the State of Florida, United
                States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall
                be subject to the exclusive jurisdiction of the state and federal courts located in Florida.
              </p>
            </Subsection>

            <Subsection title="12.2 Dispute Resolution">
              <p className="text-gray-700">
                Before initiating formal legal proceedings, both parties agree to attempt to resolve any dispute
                informally by contacting {CONTACT_EMAIL}. If informal resolution is unsuccessful after 30 days,
                either party may pursue available legal remedies.
              </p>
            </Subsection>

            <Subsection title="12.3 Entire Agreement">
              <p className="text-gray-700">
                These Terms, together with the Privacy Policy and any service agreement or order form, constitute the
                entire agreement between you and {COMPANY_NAME} regarding the Platform and supersede all prior agreements
                or understandings.
              </p>
            </Subsection>

            <Subsection title="12.4 Severability">
              <p className="text-gray-700">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue
                in full force and effect.
              </p>
            </Subsection>

            <Subsection title="12.5 Waiver">
              <p className="text-gray-700">
                Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right
                or provision.
              </p>
            </Subsection>

            <Subsection title="12.6 Changes to Terms">
              <p className="text-gray-700">
                We may update these Terms at any time. Material changes will be communicated via email to active Tenant
                account holders at least 14 days before taking effect. Continued use of the Platform after the effective
                date constitutes acceptance of the updated Terms.
              </p>
            </Subsection>
          </section>

          <Divider />

          {/* 13 - Contact */}
          <section>
            <SectionHeader icon={<Mail size={20} />} number="13" title="Contact Us" />
            <p className="text-gray-700 mb-4">
              For questions about these Terms, please contact us:
            </p>
            <div className="bg-gray-50 rounded-xl p-5 space-y-2 text-gray-700">
              <p><strong>{COMPANY_NAME}</strong></p>
              <p>Operating as: {PLATFORM_NAME}</p>
              <p>
                Legal inquiries:{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 underline">{CONTACT_EMAIL}</a>
              </p>
              <p>
                Support:{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline">{SUPPORT_EMAIL}</a>
              </p>
              <p>
                Website:{" "}
                <a href={PLATFORM_URL} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                  {PLATFORM_URL}
                </a>
              </p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} {COMPANY_NAME} · All rights reserved</p>
          <p className="mt-1">
            <a href="/privacy" className="text-blue-600 underline mr-4">Privacy Policy</a>
            <a href={PLATFORM_URL} className="text-blue-600 underline">AppointmentFlo.com</a>
          </p>
        </div>
      </main>
    </div>
  );
}

function SectionHeader({ icon, number, title }: { icon: React.ReactNode; number: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-9 h-9 bg-[#273240] rounded-lg flex items-center justify-center text-white flex-shrink-0">
        {icon}
      </div>
      <h2 className="text-xl font-bold text-gray-900">{number}. {title}</h2>
    </div>
  );
}

function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Divider() {
  return <hr className="border-gray-100" />;
}
